import {
  CATEGORIAS_CON_SEGUIMIENTO,
  ETIQUETA_CATEGORIA_DE_CUMPLIMIENTO,
  ETIQUETA_CATEGORIA_DE_CUMPLIMIENTO_EN,
  type AvisoDeCumplimiento,
  type CategoriaDeCumplimiento,
  type ResumenDeCumplimiento,
} from '@cnc/contracts';
import {useState} from 'react';
import {useSearchParams} from 'react-router';
import {useAgregarTiposSugeridos, useResumenDeCumplimiento} from '../api/cumplimiento';
import {ArchivoDeCumplimiento} from '../componentes/cumplimiento-archivo';
import {CargaDeCarpeta} from '../componentes/cumplimiento-carga';
import {FichasDeEmpresas} from '../componentes/cumplimiento-fichas';
import {FichaDeSituacion, PanelDeDocumento} from '../componentes/cumplimiento-documento';
import {ARCHIVO_INICIAL, textoDeDias, type FiltrosDeArchivo} from '../componentes/cumplimiento-estilos';
import {MatrizDeCumplimiento} from '../componentes/cumplimiento-matriz';
import {TiposDeCumplimiento} from '../componentes/cumplimiento-tipos';
import {AYUDA_RESUMEN_CUMPLIMIENTO as AR} from '../componentes/ayuda-cumplimiento';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {usePermisos} from '../componentes/sesion-contexto';
import {et, tr, type Texto} from '../i18n/idioma';
import {fecha, fechaHora, numero} from '../utilidades/formato';

// Aperturas de cuenta y Propios no tienen pestana por ahora (sus archivos estan en el Archivo).
const CATEGORIAS_EN_PESTANAS = CATEGORIAS_CON_SEGUIMIENTO;
const VISTAS = ['resumen', ...CATEGORIAS_EN_PESTANAS, 'archivo', 'fichas', 'tipos', 'cargar'] as const;
type Vista = (typeof VISTAS)[number];

// Cumplimiento (decision 0045): el archivo de compliance del equipo con sus
// vencimientos. El resumen avisa de lo vencido y lo proximo a vencer; cada
// categoria muestra una fila por vinculo; el Archivo deja encontrar cualquier
// archivo como en la carpeta de Dropbox.
export function PaginaCumplimiento() {
  const [parametros, setParametros] = useSearchParams();
  const pedida = parametros.get('vista') as Vista | null;
  const vista: Vista = pedida && VISTAS.includes(pedida) ? pedida : 'resumen';
  const permisos = usePermisos();
  const resumen = useResumenDeCumplimiento();
  const [filtrosDeArchivo, setFiltrosDeArchivo] = useState<FiltrosDeArchivo>(ARCHIVO_INICIAL);
  const categorias = et(ETIQUETA_CATEGORIA_DE_CUMPLIMIENTO, ETIQUETA_CATEGORIA_DE_CUMPLIMIENTO_EN);
  const irA = (v: Vista) => setParametros(new URLSearchParams(v === 'resumen' ? {} : {vista: v}));
  const verArchivo = (cambio: Partial<FiltrosDeArchivo>) => {
    setFiltrosDeArchivo({...ARCHIVO_INICIAL, ...cambio});
    irA('archivo');
  };
  const pestanas: Array<[Vista, string]> = [
    ['resumen', tr('Resumen', 'Summary')],
    ...CATEGORIAS_EN_PESTANAS.map(c => [c, categorias[c]] as [Vista, string]),
    ['archivo', tr('📁 Archivo', '📁 Files')],
    ['fichas', tr('Fichas de empresas', 'Company profiles')] as [Vista, string],
    ['tipos', tr('Tipos de documento', 'Document types')],
    ...(permisos.editar ? [['cargar', tr('⇪ Cargar carpeta', '⇪ Load folder')] as [Vista, string]] : []),
  ];
  return (
    <section className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold text-verde-oscuro">{tr('Cumplimiento', 'Compliance')}</h1>
        <p className="max-w-3xl text-sm text-apagado">
          {tr(
            'Los documentos de compliance de clientes, proveedores y minas, con su fecha de vencimiento. Avisa con tiempo de lo que está por vencer; cada archivo de la carpeta se puede encontrar en el Archivo, como en Dropbox.',
            'Compliance documents for customers, suppliers and mines, with their expiry date. Warns ahead of time about what is expiring; every file of the folder can be found in Files, as in Dropbox.',
          )}
          {resumen.data?.ultimaCarga &&
            tr(
              ` Última carga de la carpeta: ${fechaHora(resumen.data.ultimaCarga.fecha)}.`,
              ` Last folder load: ${fechaHora(resumen.data.ultimaCarga.fecha)}.`,
            )}
        </p>
      </div>
      <nav
        className="flex flex-wrap gap-1 border-b border-linea"
        aria-label={tr('Vistas de Cumplimiento', 'Compliance views')}
      >
        {pestanas.map(([v, texto]) => (
          <button
            key={v}
            type="button"
            aria-current={vista === v ? 'page' : undefined}
            onClick={() => irA(v)}
            className={`-mb-px rounded-t-md border px-3 py-1.5 text-sm ${
              vista === v
                ? 'border-linea border-b-white bg-white font-medium text-verde-oscuro'
                : 'border-transparent text-apagado hover:text-tinta'
            }`}
          >
            {texto}
          </button>
        ))}
      </nav>
      {vista === 'resumen' && (
        <>
          {resumen.isError && <p className="text-sm text-alerta">{resumen.error.message}</p>}
          {resumen.isPending && <p className="text-sm text-apagado">{tr('Revisando…', 'Checking…')}</p>}
          {resumen.data && <Resumen r={resumen.data} irA={irA} verArchivo={verArchivo} />}
        </>
      )}
      {(CATEGORIAS_EN_PESTANAS as readonly string[]).includes(vista) && (
        <MatrizDeCumplimiento
          key={vista}
          categoria={vista as CategoriaDeCumplimiento}
          onVerArchivo={vinculoId => verArchivo({vinculo: String(vinculoId)})}
        />
      )}
      {vista === 'archivo' && <ArchivoDeCumplimiento filtros={filtrosDeArchivo} onFiltros={setFiltrosDeArchivo} />}
      {vista === 'fichas' && <FichasDeEmpresas />}
      {vista === 'tipos' && <TiposDeCumplimiento />}
      {vista === 'cargar' && permisos.editar && <CargaDeCarpeta onTerminar={() => irA('resumen')} />}
    </section>
  );
}

function Resumen({
  r,
  irA,
  verArchivo,
}: {
  r: ResumenDeCumplimiento;
  irA: (v: Vista) => void;
  verArchivo: (cambio: Partial<FiltrosDeArchivo>) => void;
}) {
  const permisos = usePermisos();
  const sugeridos = useAgregarTiposSugeridos();
  const categorias = et(ETIQUETA_CATEGORIA_DE_CUMPLIMIENTO, ETIQUETA_CATEGORIA_DE_CUMPLIMIENTO_EN);
  const [abierto, setAbierto] = useState<{vinculoId: number; tipoId: number} | null>(null);
  if (!r.tipos)
    return (
      <div className="rounded-lg border border-linea bg-white p-5" data-prueba="cumplimiento-vacio">
        <h2 className="font-medium">{tr('Para empezar', 'To get started')}</h2>
        <ol className="mt-2 max-w-2xl list-decimal space-y-1 pl-5 text-sm text-apagado">
          <li>
            {tr(
              'Agrega la lista sugerida de tipos de documento (cámara de comercio, RUT, KYC, estados financieros, título minero…) y ajústala.',
              'Add the suggested list of document types (chamber of commerce, tax ID, KYC, financial statements, mining title…) and adjust it.',
            )}
          </li>
          <li>
            {tr(
              'Carga la carpeta de compliance: la app reconoce cada archivo, calcula su vencimiento y avisa.',
              'Load the compliance folder: the app recognizes each file, calculates its expiry and warns.',
            )}
          </li>
        </ol>
        {permisos.editar && (
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              disabled={sugeridos.isPending}
              onClick={() => sugeridos.mutate()}
              className="rounded-md bg-verde px-3 py-1.5 text-sm text-white hover:bg-verde-oscuro disabled:opacity-50"
            >
              ✚ {tr('Agregar la lista sugerida', 'Add the suggested list')}
            </button>
            <button
              type="button"
              onClick={() => irA('tipos')}
              className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel"
            >
              {tr('Ir a Tipos de documento', 'Go to Document types')}
            </button>
          </div>
        )}
      </div>
    );
  const cifras: Array<{titulo: string; valor: number; clase: string; ayuda: string; nota: Texto}> = [
    {
      titulo: tr('Vencidos', 'Expired'),
      valor: r.cuenta.vencidos,
      clase: 'border-alerta/40 bg-red-50 text-alerta',
      ayuda: tr('Hay que renovarlos ya', 'Must be renewed now'),
      nota: AR.vencidos,
    },
    {
      titulo: tr('Por vencer', 'Expiring soon'),
      valor: r.cuenta.porVencer,
      clase: 'border-amber-300 bg-amber-50 text-amber-900',
      ayuda: tr('Dentro de sus días de aviso', 'Within their warning days'),
      nota: AR.porVencer,
    },
    {
      titulo: tr('En revisión', 'Under review'),
      valor: r.cuenta.enRevision,
      clase: 'border-linea bg-papel text-tinta',
      ayuda: tr('Esperan que alguien los apruebe', 'Waiting for someone to approve them'),
      nota: AR.enRevision,
    },
    {
      titulo: tr('Faltan', 'Missing'),
      valor: r.cuenta.faltan,
      clase: 'border-alerta/30 bg-white text-alerta',
      ayuda: tr('Obligatorios sin cargar en vínculos activos', 'Required, not uploaded, in active links'),
      nota: AR.faltan,
    },
    {
      titulo: tr('Al día', 'Up to date'),
      valor: r.cuenta.alDia,
      clase: 'border-verde/30 bg-verde-claro text-verde-oscuro',
      ayuda: tr('Vigentes y lejos de vencer', 'Valid and far from expiring'),
      nota: AR.alDia,
    },
  ];
  return (
    <div className="space-y-5" data-prueba="resumen-cumplimiento">
      <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-5">
        {cifras.map(c => (
          <div key={c.titulo} className={`rounded-lg border p-3 ${c.clase}`}>
            <div className="text-xs font-semibold tracking-wide uppercase">
              {c.titulo}
              <NotaDeAyuda titulo={c.titulo} texto={c.nota} />
            </div>
            <div className="text-3xl font-semibold tabular-nums">{numero(c.valor)}</div>
            <div className="text-xs opacity-80">{c.ayuda}</div>
          </div>
        ))}
      </div>
      <p className="text-sm text-apagado">
        {tr(`${numero(r.documentos)} archivos en el archivo.`, `${numero(r.documentos)} files stored.`)}{' '}
        {r.sinClasificar > 0 && (
          <button
            type="button"
            onClick={() => verArchivo({tipo: 'sin_clasificar'})}
            className="text-verde hover:underline"
          >
            {tr(
              `${numero(r.sinClasificar)} sin clasificar: elige su tipo para que avisen →`,
              `${numero(r.sinClasificar)} unclassified: choose their type so they warn →`,
            )}
          </button>
        )}
      </p>
      <div className="-mb-3 text-xs font-semibold tracking-wide text-apagado uppercase">
        {tr('Semáforo por categoría', 'Traffic light by category')}
        <NotaDeAyuda titulo={tr('Semáforo por categoría', 'Traffic light by category')} texto={AR.semaforo} />
      </div>
      <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-5">
        {r.porCategoria.map(g => (
          <button
            key={g.categoria}
            type="button"
            onClick={() => irA(g.categoria as Vista)}
            className="rounded-lg border border-linea bg-white p-3 text-left hover:shadow-sm"
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-medium">{categorias[g.categoria]}</span>
              <span className="text-xs text-apagado">
                {tr(`${numero(g.activos)} activos`, `${numero(g.activos)} active`)}
              </span>
            </div>
            <BarraDeSemaforo g={g} />
            <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-apagado">
              <span>🔴 {numero(g.rojo)}</span>
              <span>🟡 {numero(g.amarillo)}</span>
              <span>⚪ {numero(g.gris)}</span>
              <span>🟢 {numero(g.verde)}</span>
            </div>
          </button>
        ))}
      </div>
      <ListaDeAvisos
        titulo={tr('Vencidos y por vencer', 'Expired and expiring soon')}
        vacio={tr('Nada vencido ni por vencer. ✓', 'Nothing expired or expiring soon. ✓')}
        avisos={r.vencimientos}
        onAbrir={setAbierto}
      />
      {r.enRevision.length > 0 && (
        <ListaDeAvisos titulo={tr('En revisión', 'Under review')} vacio="" avisos={r.enRevision} onAbrir={setAbierto} />
      )}
      <details className="rounded-lg border border-linea bg-white p-3">
        <summary className="cursor-pointer text-sm font-semibold tracking-wide text-apagado uppercase">
          {tr('Obligatorios que faltan', 'Missing required documents')} ({numero(r.cuenta.faltan)})
        </summary>
        <ListaDeAvisos
          titulo=""
          vacio={tr('No falta ninguno. ✓', 'None missing. ✓')}
          avisos={r.faltantes}
          onAbrir={setAbierto}
        />
      </details>
      {abierto && <PanelDeDocumento {...abierto} onCerrar={() => setAbierto(null)} />}
    </div>
  );
}

function BarraDeSemaforo({g}: {g: ResumenDeCumplimiento['porCategoria'][number]}) {
  const total = g.activos || 1;
  const partes: Array<[number, string]> = [
    [g.rojo, 'bg-alerta'],
    [g.amarillo, 'bg-dorado'],
    [g.gris, 'bg-apagado'],
    [g.verde, 'bg-verde'],
  ];
  return (
    <div className="mt-2 flex h-2 overflow-hidden rounded-full bg-papel" aria-hidden>
      {partes.map(
        ([n, clase]) => n > 0 && <div key={clase} className={clase} style={{width: `${(n / total) * 100}%`}} />,
      )}
    </div>
  );
}

function ListaDeAvisos({
  titulo,
  vacio,
  avisos,
  onAbrir,
}: {
  titulo: string;
  vacio: string;
  avisos: AvisoDeCumplimiento[];
  onAbrir: (a: {vinculoId: number; tipoId: number}) => void;
}) {
  const categorias = et(ETIQUETA_CATEGORIA_DE_CUMPLIMIENTO, ETIQUETA_CATEGORIA_DE_CUMPLIMIENTO_EN);
  const contenido = !avisos.length ? (
    <p className="mt-1 text-sm text-apagado">{vacio}</p>
  ) : (
    <table className="mt-2 w-full text-sm">
      <thead>
        <tr className="text-left text-xs text-apagado">
          <th className="py-1 pr-2 font-semibold">
            {tr('Vínculo', 'Link')}
            <NotaDeAyuda titulo={tr('Vínculo', 'Link')} texto={AR.vinculo} />
          </th>
          <th className="py-1 pr-2 font-semibold">
            {tr('Documento', 'Document')}
            <NotaDeAyuda titulo={tr('Documento', 'Document')} texto={AR.documento} />
          </th>
          <th className="py-1 pr-2 font-semibold">
            {tr('Situación', 'Status')}
            <NotaDeAyuda titulo={tr('Situación', 'Status')} texto={AR.situacion} />
          </th>
          <th className="py-1 pr-2 font-semibold">
            {tr('Vence', 'Expires')}
            <NotaDeAyuda titulo={tr('Vence', 'Expires')} texto={AR.vence} />
          </th>
          <th className="py-1 font-semibold">
            {tr('Responsable', 'Responsible')}
            <NotaDeAyuda titulo={tr('Responsable', 'Responsible')} texto={AR.responsable} />
          </th>
        </tr>
      </thead>
      <tbody>
        {avisos.map(a => (
          <tr
            key={`${a.vinculoId}-${a.tipoId}`}
            className="cursor-pointer border-t border-linea/70 hover:bg-papel"
            onClick={() => onAbrir({vinculoId: a.vinculoId, tipoId: a.tipoId})}
          >
            <td className="py-1.5 pr-2">
              <div className="font-medium text-verde">{a.vinculo}</div>
              <div className="text-xs text-apagado">{categorias[a.categoria]}</div>
            </td>
            <td className="py-1.5 pr-2">{a.tipo}</td>
            <td className="py-1.5 pr-2">
              <FichaDeSituacion situacion={a.situacion} />
            </td>
            <td className="py-1.5 pr-2 whitespace-nowrap">
              {a.venceEl ? (
                <>
                  {fecha(a.venceEl)} <span className="text-xs text-apagado">({textoDeDias(a.diasParaVencer)})</span>
                </>
              ) : (
                '—'
              )}
            </td>
            <td className="py-1.5">{a.responsable ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
  if (!titulo) return contenido;
  return (
    <section className="rounded-lg border border-linea bg-white p-3">
      <h2 className="text-sm font-semibold tracking-wide text-apagado uppercase">
        {titulo} ({numero(avisos.length)})
      </h2>
      {contenido}
    </section>
  );
}
